import { createFileRoute, Link, redirect, useNavigate, useSearch } from '@tanstack/react-router';
import { registerFinanceScreen } from '@/lib/finance-topics';
import { figuresCoverLabel, formatDateShort } from '@/lib/dates';
import { z } from 'zod';
import { isXeroEnabled } from '@/lib/feature-flags';
import { FinanceGuard } from '@/components/finance/FinanceGuard';
import { SetupHero } from '@/components/finance/SetupHero';
import { useEffect, useState } from 'react';
import { AlertCircle, CheckCircle2, Link2, RefreshCw, SlidersHorizontal, Unlink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useCurrentOrganization } from '@/hooks/use-bss-organizations';
import { useOrgRole } from '@/hooks/use-org-role';
import {
  useConnectXero,
  useDisconnectXero,
  useSyncXeroProfitAndLoss,
  useXeroStatus,
} from '@/hooks/use-xero';
import type { XeroSnapshot } from '@/api/xero';

/**
 * Xero connection screen.
 *
 * Two jobs: start the connection, and land the builder after Xero sends them
 * back. The `status` parameter Xero's callback appends is only a hint for which
 * message to lead with — it is user-supplied and could be typed by anyone — so
 * the real state always comes from the backend.
 */
const searchSchema = z.object({
  /** Set by our OAuth callback: 'connected' | 'error'. A hint, never proof. */
  status: z.string().optional(),
  /** Present when something went wrong, e.g. 'access_denied'. */
  error: z.string().optional(),
  /**
   * How many Xero organisations the consent covered. The router parses a bare
   * numeric query value as a number, so coerce rather than demand a string —
   * otherwise a successful connect (which always appends tenants=<n>) trips the
   * search validation and blows up the landing page.
   */
  tenants: z.coerce.number().optional(),
});

export const Route = createFileRoute('/finance/xero/connect')({
  // Whole feature is flag-gated. Off (or flag unresolved) → the page doesn't
  // exist as far as a builder is concerned; bounce to the finance hub. The root
  // beforeLoad warms the flag cache first, so this read is hot and synchronous.
  beforeLoad: ({ search }) => {
    if (!isXeroEnabled()) {
      throw redirect({ to: '/finance', replace: true });
    }
    // Consent returns here on purpose: this page carries the two onboarding screens (connect, then
    // "let's do your accounts"). Where to go next is decided in the component, because it depends
    // on whether this connection has ever been pulled.
  },
  component: () => (
    <FinanceGuard>
      <XeroConnectPage />
    </FinanceGuard>
  ),
  validateSearch: searchSchema,
});

/** Plain-language reasons, so a builder is never shown a raw error code. */
const ERROR_MESSAGES: Record<string, string> = {
  access_denied: "You didn't approve access, so nothing was connected. You can try again.",
  invalid_state: 'That connection link had expired. Please start again.',
  expired_state: 'That connection link had expired. Please start again.',
  token_exchange_failed: "Xero couldn't complete the connection. Please try again.",
  connections_failed: "We couldn't read your Xero organisations. Please try again.",
  no_organisations: 'No Xero organisation was shared with us. Please try again and select one.',
  not_configured: 'The Xero integration isn\'t configured yet. Please contact support.',
  token_store_failed: "We couldn't securely store your connection. Please try again.",
  connection_save_failed: "We couldn't save your connection. Please try again.",
};

/** Where a builder goes after Xero consent. Pure, so the ruling is testable without a render. */
export type ConsentLanding = 'stay' | 'accounts' | 'ledger';

/**
 * "First time only": a connection that has never been pulled gets the accounts screen, where the
 * first pull runs on screen. One that already has figures goes straight to the ledger over the
 * dashboard. Anything that is not a successful consent return stays on the connect screen.
 */
export function consentLanding(s: {
  arrivedFromConsent: boolean;
  connected: boolean;
  hasConnection: boolean;
  hasPullData: boolean;
  isLoading: boolean;
}): ConsentLanding {
  if (!s.arrivedFromConsent || !s.connected) return 'stay';
  if (s.hasPullData) return 'ledger';
  if (s.isLoading || !s.hasConnection) return 'stay';
  return 'accounts';
}

function money(v: number | null): string {
  if (v === null) return '—';
  return v.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}

function asAtLabel(iso: string): string {
  return formatDateShort(iso);
}

function Figures({ snapshot }: { snapshot: XeroSnapshot }) {
  const rows: Array<[string, number | null, boolean]> = [
    ['Revenue', snapshot.revenue, false],
    ['Cost of sales', snapshot.costOfSales, false],
    ['Gross profit', snapshot.grossProfit, true],
    ['Operating expenses', snapshot.operatingExpenses, false],
    ['Net profit', snapshot.netProfit, true],
  ];

  return (
    <div className="space-y-3">
      <dl className="divide-y divide-border">
        {rows.map(([label, value, emphasise]) => (
          <div key={label} className="flex items-baseline justify-between py-2">
            <dt
              className={emphasise
                ? 'font-medium text-foreground'
                : 'text-sm text-muted-foreground'}
            >
              {label}
            </dt>
            <dd
              className={emphasise
                ? 'text-lg font-semibold tabular-nums'
                : 'text-sm tabular-nums text-muted-foreground'}
            >
              {money(value)}
            </dd>
          </div>
        ))}
      </dl>
      <p className="text-xs text-muted-foreground">
        These are the figures Xero calculated {figuresCoverLabel(snapshot.asAt, snapshot.fetchedAt)}.
      </p>
    </div>
  );
}

function XeroConnectPage() {
  const { status, error } = useSearch({ from: '/finance/xero/connect' });
  const { data: orgData } = useCurrentOrganization();
  const organizationId = orgData?.organization?.id ?? null;
  // A Xero screen: the normal Alfie answers in finance mode here (Tim, 31 Oct 2026). No topics of
  // its own, so questions go with the figures only.
  useEffect(() => (organizationId ? registerFinanceScreen('xero-connect-page', organizationId) : undefined), [organizationId]);
  const { isAdminOrOwner } = useOrgRole();

  const { data, isLoading, isError } = useXeroStatus(organizationId);
  const connect = useConnectXero();
  const sync = useSyncXeroProfitAndLoss(organizationId);
  const disconnect = useDisconnectXero(organizationId);
  const [confirmingDisconnect, setConfirmingDisconnect] = useState(false);

  // Prefer the active connection — the one the dashboard/sync act on.
  const connection = data?.connections.find((c) => c.isActive && c.status === 'connected') ??
    data?.connections.find((c) => c.status === 'connected') ??
    data?.connections[0] ?? null;
  const snapshot = data?.latestSnapshot ?? null;
  const isDisconnected = !!connection && connection.status === 'disconnected';
  const otherConnected = (data?.connections.filter((c) =>
    c.status === 'connected' && c.xeroTenantId !== connection?.xeroTenantId
  ).length) ?? 0;

  const navigate = useNavigate();
  const arrivedFromConsent = status === 'connected';
  // "First time only": a connection that has never been pulled has no figures and no cached
  // mapping data, so it gets the pull screen. One that has either goes straight to the ledger.
  const hasPullData = Boolean(connection?.lastPullAt) || Boolean(snapshot);

  const landing = consentLanding({
    arrivedFromConsent,
    connected: Boolean(data?.connected),
    hasConnection: Boolean(connection),
    hasPullData,
    isLoading,
  });

  useEffect(() => {
    if (landing === 'ledger') {
      navigate({ to: '/finance', search: { setup: true }, replace: true });
    }
  }, [landing, navigate]);

  // SCREEN 2 — straight after consent, nothing pulled yet. The whole first pull happens here, on
  // screen, so the builder watches it rather than meeting an inert ledger.
  if (landing === 'accounts' && connection) {
    return (
      <div className="mx-auto w-full max-w-5xl p-4">
        <SetupHero
          working={sync.isPending}
          title="Now let's do your accounts"
          lead={`Connected to ${
            connection.tenantName ?? 'Xero'
          }. Alfie will read your chart of accounts and pull your figures, then ask you about the handful he cannot place on his own.`}
        >
          {sync.isPending
            ? (
              <ul className="space-y-2 text-[14px] text-white/85" role="status">
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#FF8E2C]" />
                  Reading your chart of accounts
                </li>
                <li className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-[#FF8E2C]" />
                  Pulling twelve months of figures
                </li>
                <li className="flex items-center gap-2 text-white/60">
                  <span className="h-1.5 w-1.5 rounded-full bg-white/30" />
                  Then Alfie drafts what he can
                </li>
              </ul>
            )
            : (
              <Button
                size="lg"
                className="min-h-[48px] border-0 bg-[#FF8E2C] text-[#1C1B19] hover:bg-[#FFBD59]"
                onClick={() =>
                  sync.mutate(undefined, {
                    // Straight into the ledger, over the dashboard the figures just landed on.
                    onSuccess: () => navigate({ to: '/finance', search: { setup: true } }),
                    // A failed first pull must not strand them: the ledger still works, it just
                    // has no figures beside each account yet.
                    onError: () => navigate({ to: '/finance', search: { setup: true } }),
                  })}
              >
                Connect my accounts
              </Button>
            )}
          <p className="mt-4 text-[12.5px] text-white/60">
            It takes a minute or two the first time. After that Alfie keeps up on his own.
          </p>
        </SetupHero>
      </div>
    );
  }

  // SCREEN 1 — not connected yet.
  if (!isLoading && !isError && !data?.connected) {
    return (
      <div className="mx-auto w-full max-w-5xl space-y-4 p-4">
        {status === 'error' && (
          <Card className="border-destructive/40 bg-destructive/5 p-4">
            <div className="flex gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
              <p className="text-sm">
                {ERROR_MESSAGES[error ?? ''] ?? "That didn't work. Please try again."}
              </p>
            </div>
          </Card>
        )}
        <SetupHero
          title="Your real numbers, straight from Xero"
          lead="Connect Xero once and Alfie reads your accounts, works out what each one is for, and keeps your figures current — margin, cash, where the money actually went."
        >
          {isAdminOrOwner
            ? (
              <Button
                size="lg"
                className="min-h-[48px] border-0 bg-[#FF8E2C] text-[#1C1B19] hover:bg-[#FFBD59]"
                onClick={() => organizationId && connect.mutate(organizationId)}
                disabled={!organizationId || connect.isPending}
              >
                <Link2 className="mr-2 h-4 w-4" />
                {connect.isPending ? 'Opening Xero…' : 'Connect Xero'}
              </Button>
            )
            : (
              <p className="text-[14px] text-white/80">
                Ask an owner or admin of your organisation to connect Xero.
              </p>
            )}
          {connect.isError && (
            <p className="mt-3 text-[13px] text-white/90">
              {(connect.error as Error)?.message ?? "We couldn't start the connection."}
            </p>
          )}
        </SetupHero>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-2xl space-y-4 p-4">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">Xero</h1>
        <p className="text-sm text-muted-foreground">
          Connect your accounts so Alfie can show you your real numbers.
        </p>
      </header>

      {/* The redirect's own message, shown first but never trusted as state. */}
      {status === 'error' && (
        <Card className="border-destructive/40 bg-destructive/5 p-4">
          <div className="flex gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            <p className="text-sm">
              {ERROR_MESSAGES[error ?? ''] ?? "That didn't work. Please try again."}
            </p>
          </div>
        </Card>
      )}

      <Card className="p-5">
        {isLoading
          ? (
            <div className="space-y-3">
              <Skeleton className="h-5 w-40" />
              <Skeleton className="h-4 w-64" />
            </div>
          )
          : isError
          ? (
            <p className="text-sm text-muted-foreground">
              We couldn't check your Xero connection just now. Please refresh the page.
            </p>
          )
          : data?.connected && connection
          ? (
            <div className="space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success" />
                  <div>
                    <p className="font-medium">Connected to {connection.tenantName ?? 'Xero'}</p>
                    <p className="text-sm text-muted-foreground">
                      {connection.lastPullAt
                        ? `Last updated ${asAtLabel(connection.lastPullAt)}`
                        : 'Not yet updated'}
                    </p>
                    {otherConnected > 0 && (
                      <p className="text-xs text-muted-foreground">
                        +{otherConnected} other Xero organisation{otherConnected > 1 ? 's' : ''}{' '}
                        connected · showing your active one
                      </p>
                    )}
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="min-h-[44px] sm:min-h-9"
                  onClick={() => sync.mutate(undefined)}
                  disabled={sync.isPending}
                >
                  <RefreshCw className={`mr-2 h-4 w-4 ${sync.isPending ? 'animate-spin' : ''}`} />
                  {sync.isPending ? 'Updating…' : 'Refresh'}
                </Button>
              </div>

              {snapshot
                ? <Figures snapshot={snapshot} />
                : (
                  <p className="text-sm text-muted-foreground">
                    No figures yet — choose Refresh to pull them from Xero.
                  </p>
                )}

              {sync.isError && (
                <p className="text-sm text-destructive">
                  {(sync.error as Error)?.message ?? "We couldn't update the figures."}
                </p>
              )}

              {/* Into the mapping wizard — how the builder turns their chart of
                  accounts into the metrics Alfie can compute. */}
              <Link
                to="/finance"
            search={{ setup: true }}
                className="mt-4 flex items-center justify-between rounded-xl border border-border bg-card/60 px-4 py-3 transition-colors hover:border-primary/40"
              >
                <span className="flex items-center gap-3">
                  <SlidersHorizontal className="h-5 w-5 text-primary" aria-hidden />
                  <span className="text-sm">
                    <span className="block font-medium text-foreground">Set up your accounts</span>
                    <span className="block text-muted-foreground">
                      Help Alfie unlock more of your metrics
                    </span>
                  </span>
                </span>
                <span aria-hidden className="text-muted-foreground">→</span>
              </Link>

              {/* Disconnect — owner/admin only, two-step so it can't be a mis-click. */}
              {isAdminOrOwner && (
                <div className="border-t border-border pt-3">
                  {confirmingDisconnect
                    ? (
                      <div className="space-y-2">
                        <p className="text-sm text-muted-foreground">
                          Disconnect {connection.tenantName ?? 'Xero'}? Alfie will stop reading your
                          numbers until you reconnect. Your saved account setup can be kept — a
                          reconnect finds it again — or deleted along with the connection.
                        </p>
                        <div className="flex flex-wrap items-center gap-3">
                          <Button
                            variant="destructive"
                            size="sm"
                            className="min-h-[44px] sm:min-h-9"
                            onClick={() =>
                              disconnect.mutate({ deleteMappings: false }, {
                                onSuccess: () => setConfirmingDisconnect(false),
                              })}
                            disabled={disconnect.isPending}
                          >
                            {disconnect.isPending ? 'Disconnecting…' : 'Disconnect, keep my setup'}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            className="min-h-[44px] text-danger hover:text-danger sm:min-h-9"
                            onClick={() =>
                              disconnect.mutate({ deleteMappings: true }, {
                                onSuccess: () => setConfirmingDisconnect(false),
                              })}
                            disabled={disconnect.isPending}
                          >
                            Disconnect and delete my setup
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="min-h-[44px] sm:min-h-9"
                            onClick={() => setConfirmingDisconnect(false)}
                            disabled={disconnect.isPending}
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    )
                    : (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="min-h-[44px] text-muted-foreground hover:text-destructive sm:min-h-9"
                        onClick={() => setConfirmingDisconnect(true)}
                      >
                        <Unlink className="mr-2 h-4 w-4" />
                        Disconnect Xero
                      </Button>
                    )}
                  {disconnect.isError && (
                    <p className="mt-2 text-sm text-destructive">
                      {(disconnect.error as Error)?.message ?? "We couldn't disconnect."}
                    </p>
                  )}
                </div>
              )}
            </div>
          )
          : (
            <div className="space-y-4">
              {/* Disconnected still shows the last known figures — never a blank screen. */}
              {isDisconnected && (
                <div className="flex gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-warning" />
                  <div>
                    <p className="font-medium">Your Xero connection needs renewing</p>
                    <p className="text-sm text-muted-foreground">
                      Access was revoked or expired. Reconnect to bring your numbers up to date.
                    </p>
                  </div>
                </div>
              )}

              {snapshot && (
                <>
                  <Figures snapshot={snapshot} />
                  <p className="text-xs text-muted-foreground">
                    Showing the last figures we have. Reconnect to update them.
                  </p>
                </>
              )}

              {isAdminOrOwner
                ? (
                  <Button
                    className="min-h-[44px] w-full sm:w-auto"
                    onClick={() => organizationId && connect.mutate(organizationId)}
                    disabled={!organizationId || connect.isPending}
                  >
                    <Link2 className="mr-2 h-4 w-4" />
                    {connect.isPending
                      ? 'Opening Xero…'
                      : isDisconnected
                      ? 'Reconnect Xero'
                      : 'Connect Xero'}
                  </Button>
                )
                : (
                  <p className="text-sm text-muted-foreground">
                    Ask an owner or admin of your organisation to connect Xero.
                  </p>
                )}

              {connect.isError && (
                <p className="text-sm text-destructive">
                  {(connect.error as Error)?.message ?? "We couldn't start the connection."}
                </p>
              )}
            </div>
          )}
      </Card>
    </div>
  );
}
